import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Dialog, ItTag, isContractor } from './TodoPeople';
import { Icon } from './icons';
import { swatch } from './MoreMenu';
import {
  applyOptions, applyTemplate, errorText, guessPerson, previewTemplate, suggestListName
} from '../../lib/todoTemplates';

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** "Fri 13 Nov", with the year when it is not this one - always the weekday, since that is the point. */
const dayOf = (v, now = new Date()) => {
  if (!v) return '';
  const [y, m, d] = String(v).split('-').map(Number);
  const at = new Date(y, m - 1, d);
  return `${WD[at.getDay()]} ${d} ${MON[m - 1]}${y !== now.getFullYear() ? ` ${y}` : ''}`;
};

/**
 * The date the dialog starts on: today for a template counted from a start
 * date, and for one counted back from a due date the 15th of next month -
 * the day most of Malta's returns are due (VAT, the FS5), so the commonest
 * answer is already there.
 */
const firstDate = (anchor, now = new Date()) => (anchor === 'due'
  ? iso(new Date(now.getFullYear(), now.getMonth() + 1, 15)) : iso(now));

/**
 * USING A TEMPLATE (071): when, who, where - and what it will make.
 *
 *   WHEN   the start date, or the due date, as the template counts.
 *   WHO    each of the template's roles given to somebody: me, or anybody
 *          in the chat directory (exactly the people the server lets this
 *          person put on a list). A role left as "Nobody yet" leaves its
 *          steps unassigned. A role named after somebody - a list saved as
 *          a template names them that way - starts on that person.
 *   WHERE  a new list (its name suggested from the template, the client and
 *          the period, until it is typed over) or one of my lists, into a
 *          new section if one is named. "Share it with the people given a
 *          role" puts them on the list, without which they cannot be given
 *          a step on it.
 *   CLIENT where the server has clients on to-dos (072): on the new list and
 *          every to-do.
 *
 * THE PREVIEW is the server's own plan (POST /todo-templates/:id/preview),
 * asked again a moment after anything changes, and Create sends the same
 * body to /apply - so what is shown is what is made, dates and people and
 * all. What will be left out, and why, is said above it.
 */
export default function TemplateApply({ template: t, lists, people, selfKey, onClose, onApplied }) {
  const roles = useMemo(() => t.roles || [], [t.roles]);
  const [date, setDate] = useState(() => firstDate(t.anchor));
  const [who, setWho] = useState(() => Object.fromEntries(roles.map((r) => [r, guessPerson(r, people)?.key || ''])));
  const [target, setTarget] = useState('new');
  const [period, setPeriod] = useState('');
  const [clientId, setClientId] = useState('');
  const [name, setName] = useState(t.name);
  const [named, setNamed] = useState(false);   // typed over: no longer suggested
  const [share, setShare] = useState(true);
  const usable = useMemo(() => (lists || []).filter((l) => !l.archived_at), [lists]);
  const [listId, setListId] = useState(() => (usable.find((l) => !l.is_inbox) || usable[0] || {}).id || '');
  const [section, setSection] = useState('');
  const [options, setOptions] = useState({ clients: false, companies: [] });
  const [plan, setPlan] = useState(null);
  const [planErr, setPlanErr] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    applyOptions().then((o) => setOptions({ clients: Boolean(o?.clients), companies: o?.companies || [] }))
      .catch(() => setOptions({ clients: false, companies: [] }));
  }, []);

  // A role named after somebody: once the directory arrives, that person.
  useEffect(() => {
    if (!people) return;
    setWho((prev) => Object.fromEntries(roles.map((r) => [r, prev[r] || guessPerson(r, people)?.key || ''])));
  }, [people, roles]);

  const client = options.companies.find((c) => c.id === clientId) || null;
  useEffect(() => {
    if (!named) setName(suggestListName(t.name, client?.name, period));
  }, [named, t.name, client, period]);

  const chosenList = usable.find((l) => l.id === listId) || null;
  // Only the owner of a list says who is on it, and the Inbox is nobody else's.
  const mayShare = target === 'new' || Boolean(chosenList && !chosenList.is_inbox && chosenList.role === 'owner');

  const body = useMemo(() => ({
    anchor_date: date,
    roles: Object.fromEntries(Object.entries(who).filter(([, k]) => k)),
    target,
    ...(target === 'new' ? { list_name: name } : { list_id: listId, section_name: section.trim() || null }),
    share,
    company_id: clientId || null
  }), [date, who, target, name, listId, section, share, clientId]);

  const ready = /^\d{4}-\d{2}-\d{2}$/.test(date) && (target === 'new' ? name.trim() : listId);

  // The preview: asked a moment after the last change; an older answer that
  // arrives after a newer question is dropped.
  useEffect(() => {
    if (!ready) { setPlan(null); setPlanErr(null); return undefined; }
    const n = ++seq.current;
    setAsking(true);
    const timer = setTimeout(() => {
      previewTemplate(t.id, body)
        .then((p) => { if (n === seq.current) { setPlan(p); setPlanErr(null); } })
        .catch((err) => { if (n === seq.current) { setPlan(null); setPlanErr(errorText(err, 'Could not work out the dates')); } })
        .finally(() => { if (n === seq.current) setAsking(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [t.id, body, ready]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await applyTemplate(t.id, body);
      onApplied(r);
    } catch (err) {
      setError(errorText(err, 'Could not make the to-dos'));
      setBusy(false);
    }
  };

  const everyoneMe = () => setWho(Object.fromEntries(roles.map((r) => [r, selfKey || ''])));
  const directory = useMemo(() => (people || []).slice()
    .sort((a, b) => (isContractor(a) - isContractor(b)) || String(a.name).localeCompare(String(b.name))), [people]);

  const steps = plan?.steps || [];
  const dates = steps.map((s) => s.due_date).filter(Boolean).sort();
  const n = plan?.count ?? t.steps?.length ?? 0;

  return (
    <Dialog label={`Use “${t.name}”`} onClose={onClose} className={`tpl-apply ${swatch(t.color)}`}>
      {error && <p className="tds-error" role="alert">{error}</p>}
      <div className="tpl-apply-body">
        <div className="tpl-apply-form">
          <fieldset className="tpl-apply-group">
            <legend>When</legend>
            <label className="tds-field">
              <span>{t.anchor === 'due' ? 'Due date' : 'Start date'}</span>
              <input type="date" value={date} required onChange={(e) => setDate(e.target.value)} />
            </label>
            <small className="tpl-hint">
              {t.anchor === 'due' ? 'Steps are counted back from it' : 'Steps are counted from it'}
              {t.day_kind === 'working' ? ', in working days - weekends and Malta’s public holidays are skipped.' : ', in calendar days.'}
            </small>
          </fieldset>

          {roles.length > 0 && (
            <fieldset className="tpl-apply-group">
              <legend>Who</legend>
              {roles.map((r) => (
                <label key={r} className="tpl-who">
                  <span className="tpl-who-role"><Icon name="person" size={13} />{r}</span>
                  <select value={who[r] || ''} aria-label={`Who plays ${r}`}
                          onChange={(e) => setWho((prev) => ({ ...prev, [r]: e.target.value }))}>
                    <option value="">Nobody yet</option>
                    {selfKey && <option value={selfKey}>Me</option>}
                    {directory.map((p) => (
                      <option key={p.key} value={p.key}>{p.name}{isContractor(p) ? ' (IT)' : ''}</option>
                    ))}
                  </select>
                </label>
              ))}
              {selfKey && (
                <button type="button" className="tds-link tpl-all-me" onClick={everyoneMe}>Give every role to me</button>
              )}
              {people === null && <p className="tds-quiet">Loading people…</p>}
            </fieldset>
          )}

          <fieldset className="tpl-apply-group">
            <legend>Where</legend>
            <span className="tdb-switch tpl-seg" role="group" aria-label="Where the to-dos go">
              <button type="button" aria-pressed={target === 'new'} onClick={() => setTarget('new')}>A new list</button>
              <button type="button" aria-pressed={target === 'existing'} onClick={() => setTarget('existing')}>One of my lists</button>
            </span>
            {options.clients && (
              <label className="tds-field">
                <span>Client</span>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">No client</option>
                  {options.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            {target === 'new' ? (
              <>
                <label className="tds-field">
                  <span>Period (optional)</span>
                  <input type="text" value={period} maxLength={30} placeholder="Q3 2026, October 2026…"
                         onChange={(e) => setPeriod(e.target.value)} />
                </label>
                <label className="tds-field">
                  <span>List name</span>
                  <input type="text" value={name} maxLength={80}
                         onChange={(e) => { setName(e.target.value); setNamed(true); }} />
                </label>
              </>
            ) : (
              <>
                <label className="tds-field">
                  <span>List</span>
                  <select value={listId} onChange={(e) => setListId(e.target.value)}>
                    {usable.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </label>
                <label className="tds-field">
                  <span>New section (optional)</span>
                  <input type="text" value={section} maxLength={80} placeholder={t.name}
                         onChange={(e) => setSection(e.target.value)} />
                </label>
              </>
            )}
            {mayShare && (
              <label className="tpl-check">
                <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
                <span>Share {target === 'new' ? 'it' : 'the list'} with the people given a role</span>
              </label>
            )}
          </fieldset>
        </div>

        <section className="tpl-preview" aria-label="Preview" aria-busy={asking}>
          <h4 className="tpl-preview-head">
            Preview<span className="tpl-group-n">{plan ? `${n} to-do${n === 1 ? '' : 's'}` : ''}</span>
            {asking && <span className="tpl-preview-busy">Working out the dates…</span>}
          </h4>
          {!ready ? (
            <p className="tds-quiet">{target === 'new' ? 'Give the date and the list’s name to see the dates.' : 'Pick a date to see the dates.'}</p>
          ) : planErr ? (
            <p className="tds-error" role="alert">{planErr}</p>
          ) : !plan ? (
            <p className="tds-quiet">Working out the dates…</p>
          ) : (
            <>
              <p className="tpl-preview-sum">
                {target === 'new' ? <>Into a new list, <strong>{plan.target.list_name}</strong></>
                  : <>Into <strong>{plan.target.list_name}</strong>{plan.target.section_name ? <>, section <strong>{plan.target.section_name}</strong></> : ''}</>}
                {plan.company ? <>, for <strong>{plan.company.name}</strong></> : null}
                {dates.length ? <>, from {dayOf(dates[0])} to {dayOf(dates[dates.length - 1])}</> : null}.
                {plan.target.adds.length > 0 && <> Shared with {plan.target.adds.map((a) => a.name).join(', ')}.</>}
              </p>
              {plan.warnings.length > 0 && (
                <ul className="tpl-warnings" aria-label="Left out">
                  {plan.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              )}
              <ol className="tpl-prev">
                {steps.map((s, i) => {
                  const prevTop = steps.slice(0, i).reverse().find((x) => !x.parent);
                  const mark = !s.parent && s.section && (!prevTop || prevTop.section !== s.section);
                  return (
                    <React.Fragment key={s.step_id}>
                      {mark && <li className="tpl-prev-sec">{s.section}</li>}
                      <li className="tpl-prev-row" data-preview-step={s.step_id} style={{ '--tpl-level': s.level }}>
                        <span className={`tpl-step-pri is-p${s.priority || 4}`} aria-hidden="true" />
                        <span className="tpl-prev-title">{s.title}</span>
                        <span className="tpl-prev-date">
                          {s.due_date ? dayOf(s.due_date) : 'No date'}{s.due_time ? ` ${s.due_time}` : ''}
                        </span>
                        <span className={`tpl-prev-who${s.assignee_key ? '' : ' is-none'}`}>
                          {s.assignee_key ? (
                            <>
                              <Avatar name={s.assignee_name} who={s.assignee_key} />
                              <span className="tpl-prev-name">{s.assignee_key === selfKey ? 'You' : s.assignee_name}</span>
                              {String(s.assignee_key).startsWith('c:') && <ItTag />}
                            </>
                          ) : <span className="tpl-prev-name">{s.role ? `${s.role} - nobody` : 'Unassigned'}</span>}
                        </span>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
            </>
          )}
        </section>
      </div>
      <div className="tds-dialog-foot">
        <button type="button" className="btn btn-outline tds-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary tds-btn tpl-create" disabled={busy || !plan || asking || !ready}
                onClick={create}>
          {busy ? 'Making them…' : `Create ${n} to-do${n === 1 ? '' : 's'}`}
        </button>
      </div>
    </Dialog>
  );
}
